import React from 'react';
import type { Job } from '../graphql/types';
import { USE_CASE_LABELS, ALLOWED_USE_CASES, type UseCase } from '../titleAnalysis/inputs';

/**
 * OrderRail: order summary at the top of the TR Rail. Shows the search order
 * as a definition list: loan number, client, borrower, property address,
 * county and state, effective date, plus APN and use case when present.
 *
 * Requirement: 3.1.
 */
export type OrderRailJob = Pick<
  Job,
  | 'loanNumber'
  | 'clientName'
  | 'borrowerName'
  | 'propertyAddress'
  | 'propertyCounty'
  | 'propertyState'
  | 'effectiveDate'
  | 'apn'
  | 'useCase'
>;

export interface OrderRailProps {
  job: OrderRailJob;
  className?: string;
}

const PLACEHOLDER = '—';

function clean(value: string | null | undefined): string {
  return value?.trim() ?? '';
}

/** "Cook County, IL" / "Cook County" / "IL" / "". */
export function formatCountyState(
  county: string | null | undefined,
  state: string | null | undefined
): string {
  const c = clean(county);
  const s = clean(state);
  const countyText = c ? (/\bcounty$/i.test(c) ? c : `${c} County`) : '';
  return [countyText, s].filter(Boolean).join(', ');
}

/** "2026-03-14" → "Mar 14, 2026". Anything unparseable is returned as given. */
export function formatEffectiveDate(value: string | null | undefined): string {
  const raw = clean(value);
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  if (!match) return raw;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  if (Number.isNaN(date.getTime())) return raw;
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

function useCaseLabel(value: string | null | undefined): string {
  const raw = clean(value);
  return (ALLOWED_USE_CASES as readonly string[]).includes(raw)
    ? USE_CASE_LABELS[raw as UseCase]
    : raw;
}

export const OrderRail: React.FC<OrderRailProps> = ({ job, className = '' }) => {
  const rows: Array<{ key: string; label: string; value: string; optional?: boolean }> = [
    { key: 'loanNumber', label: 'Loan number', value: clean(job.loanNumber) },
    { key: 'clientName', label: 'Client', value: clean(job.clientName) },
    { key: 'borrowerName', label: 'Borrower', value: clean(job.borrowerName) },
    { key: 'propertyAddress', label: 'Property address', value: clean(job.propertyAddress) },
    {
      key: 'countyState',
      label: 'County / state',
      value: formatCountyState(job.propertyCounty, job.propertyState),
    },
    { key: 'effectiveDate', label: 'Effective date', value: formatEffectiveDate(job.effectiveDate) },
    { key: 'apn', label: 'APN', value: clean(job.apn), optional: true },
    { key: 'useCase', label: 'Use case', value: useCaseLabel(job.useCase), optional: true },
  ];

  return (
    <section aria-label="Order summary" className={`border-b border-gray-100 p-4 ${className}`.trim()}>
      <h2 className="mb-3 text-[11px] font-bold uppercase tracking-[0.1em] text-gray-500">
        Order
      </h2>
      <dl className="space-y-3">
        {rows
          .filter((row) => !row.optional || row.value)
          .map((row) => (
            <div key={row.key} data-testid={`order-${row.key}`}>
              <dt className="text-xs text-gray-500">{row.label}</dt>
              <dd className="mt-0.5 break-words text-sm font-bold text-gray-900">
                {row.value || PLACEHOLDER}
              </dd>
            </div>
          ))}
      </dl>
    </section>
  );
};

export default OrderRail;
